import {
  adminFoodRequestApprovalSchema,
  adminFoodRequestBulkNamesResultSchema,
  adminFoodRequestDetailSchema,
  adminFoodRequestSchema,
  adminFoodRequestSummarySchema,
  type AdminFoodRequest,
  type AdminFoodRequestApproval,
  type AdminFoodRequestApproveFixRequest,
  type AdminFoodRequestApproveNameRequest,
  type AdminFoodRequestBulkNamesResult,
  type AdminFoodRequestCreateFoodRequest,
  type AdminFoodRequestDetail,
  type AdminFoodRequestResolveRequest,
  type AdminFoodRequestSummary,
  type FoodRequestKindName,
  type RecipeConfidenceName,
} from '@steado/admin-contract/foods';
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient } from '@/lib/api/client';
import { revertBatch } from '@/screens/catalog-history/catalog-history-query';

import type { QueueStatus } from './food-requests-search';

/**
 * Reads and writes for food requests: the queue, a request's evidence, and
 * every answer. Each approval lands in History as one batch under the
 * request's name, so Undo is that batch's revert followed by `reopen`.
 */

/** The whole open queue in one page: it is a triage list, and the server caps a page at 100. */
const QUEUE_LIMIT = 100;

export interface QueueFilters {
  readonly status: QueueStatus;
  readonly kind?: FoodRequestKindName | undefined;
  readonly confidence?: RecipeConfidenceName | undefined;
  readonly q?: string | undefined;
}

/** "Ready to approve" first, then the most people: the order the queue reads in. */
export function queueQueryOptions(api: ApiClient, filters: QueueFilters) {
  const query = {
    status: filters.status === 'researching' ? 'open' : filters.status,
    draftStatus: filters.status === 'researching' ? 'queued' : undefined,
    kind: filters.kind,
    confidence: filters.confidence,
    q: filters.q,
    order: filters.status === 'open' || filters.status === 'researching' ? 'ready' : 'recent',
    limit: QUEUE_LIMIT,
  };
  return queryOptions({
    queryKey: ['admin', 'food-requests', 'list', query],
    queryFn: async (): Promise<AdminFoodRequest[]> =>
      (
        await api.requestWithMeta({
          path: '/admin/food-requests',
          query,
          schema: z.array(adminFoodRequestSchema),
        })
      ).data,
    placeholderData: (previous) => previous,
  });
}

/** The latest answers, for the empty queue's "Recently answered". */
export function recentlyAnsweredQueryOptions(api: ApiClient) {
  const query = { order: 'recent', limit: 20 };
  return queryOptions({
    queryKey: ['admin', 'food-requests', 'list', query],
    queryFn: async (): Promise<AdminFoodRequest[]> =>
      (
        await api.requestWithMeta({
          path: '/admin/food-requests',
          query,
          schema: z.array(adminFoodRequestSchema),
        })
      ).data
        .filter((request) => request.status !== 'open' && request.answeredAt !== null)
        .sort((a, b) => (b.answeredAt ?? '').localeCompare(a.answeredAt ?? ''))
        .slice(0, 3),
  });
}

export function summaryQueryOptions(api: ApiClient) {
  return queryOptions({
    queryKey: ['admin', 'food-requests', 'summary'],
    queryFn: (): Promise<AdminFoodRequestSummary> =>
      api.request({ path: '/admin/food-requests/summary', schema: adminFoodRequestSummarySchema }),
  });
}

export function requestQueryOptions(api: ApiClient, id: string | null) {
  return queryOptions({
    queryKey: ['admin', 'food-requests', 'detail', id],
    queryFn: (): Promise<AdminFoodRequestDetail> =>
      api.request({ path: `/admin/food-requests/${id ?? ''}`, schema: adminFoodRequestDetailSchema }),
    enabled: id !== null,
  });
}

function post<T extends z.ZodTypeAny>(
  api: ApiClient,
  path: string,
  body: unknown,
  schema: T,
): Promise<z.infer<T>> {
  return api.request({ path, method: 'POST', body, schema });
}

export function approveName(
  api: ApiClient,
  id: string,
  body: AdminFoodRequestApproveNameRequest,
): Promise<AdminFoodRequestApproval> {
  return post(api, `/admin/food-requests/${id}/approve-name`, body, adminFoodRequestApprovalSchema);
}

export function approveNames(
  api: ApiClient,
  ids: readonly string[],
): Promise<AdminFoodRequestBulkNamesResult> {
  return post(api, '/admin/food-requests/approve-names', { ids }, adminFoodRequestBulkNamesResultSchema);
}

export function createFood(
  api: ApiClient,
  id: string,
  body: AdminFoodRequestCreateFoodRequest,
): Promise<AdminFoodRequestApproval> {
  return post(api, `/admin/food-requests/${id}/create-food`, body, adminFoodRequestApprovalSchema);
}

export function approveFix(
  api: ApiClient,
  id: string,
  body: AdminFoodRequestApproveFixRequest,
): Promise<AdminFoodRequestApproval> {
  return post(api, `/admin/food-requests/${id}/approve-fix`, body, adminFoodRequestApprovalSchema);
}

export function researchAgain(api: ApiClient, id: string, note: string): Promise<AdminFoodRequest> {
  const trimmed = note.trim();
  return post(
    api,
    `/admin/food-requests/${id}/research`,
    trimmed === '' ? {} : { note: trimmed },
    adminFoodRequestSchema,
  );
}

export function keepEstimate(api: ApiClient, id: string, reason: string): Promise<AdminFoodRequest> {
  return post(
    api,
    `/admin/food-requests/${id}/keep-estimate`,
    { reason: reason.trim() },
    adminFoodRequestSchema,
  );
}

export function resolveFoodRequest(
  api: ApiClient,
  id: string,
  body: AdminFoodRequestResolveRequest,
): Promise<AdminFoodRequest> {
  return post(api, `/admin/food-requests/${id}/resolve`, body, adminFoodRequestSchema);
}

export function rejectFoodRequest(api: ApiClient, id: string, reason: string): Promise<AdminFoodRequest> {
  return post(api, `/admin/food-requests/${id}/reject`, { reason: reason.trim() }, adminFoodRequestSchema);
}

/** Back in the queue, once nothing its answer wrote is still in the catalog. */
export function reopenFoodRequest(api: ApiClient, id: string): Promise<AdminFoodRequest> {
  return post(api, `/admin/food-requests/${id}/reopen`, {}, adminFoodRequestSchema);
}

/** After any answer: the queue, its summary and the sidebar's count, and the catalog it changed. */
export function invalidateRequests(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['admin', 'food-requests'] });
  void queryClient.invalidateQueries({ queryKey: ['admin', 'scope-counts'] });
  void queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
}

/**
 * Undo for answers: each approval's History batch reverted, then the request
 * reopened, in that order, because a request whose change is still in the
 * catalog will not reopen. A rejection, or a name pointed at another food,
 * wrote nothing to revert (`batchId` null).
 */
export async function undoApprovals(
  api: ApiClient,
  approvals: readonly { readonly batchId: string | null; readonly requestId: string }[],
): Promise<void> {
  for (const approval of approvals) {
    if (approval.batchId !== null) await revertBatch(api, approval.batchId);
    await reopenFoodRequest(api, approval.requestId);
  }
}
